import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@/lib/i18n';

function SocialIcon({ href, label, children }) {
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" aria-label={label}
      className="w-9 h-9 rounded-full bg-foreground/5 hover:bg-terracotta/10 flex items-center justify-center transition-colors group">
      {children}
    </a>
  );
}

export default function Footer() {
  const { language } = useLanguage();
  const nav = useNavigate();
  const de = language === 'de';

  return (
    <footer className="bg-warmgray/40 border-t border-border/30" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-6 py-12 md:py-16">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12">
          {/* Brand */}
          <div className="col-span-2 md:col-span-1">
            <span className="font-display text-2xl font-semibold cursor-pointer" onClick={() => nav('/')}>
              <span className="text-terracotta">bel</span>osophie
            </span>
            <p className="font-body text-sm text-foreground/50 mt-3 leading-relaxed">
              {de ? 'Ganzheitliches Wellness-Coaching für Frauen 40+. Metabolisch. Mediterran. Mit Genuss.' : 'Holistic wellness coaching for women 40+. Metabolic. Mediterranean. With pleasure.'}
            </p>
            {/* Social Media */}
            <div className="flex gap-3 mt-4" data-testid="social-links">
              <SocialIcon href="https://facebook.com/belosophie" label="Facebook">
                <svg className="w-4 h-4 text-foreground/40 group-hover:text-terracotta transition-colors" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
              </SocialIcon>
              <SocialIcon href="https://instagram.com/belosophie" label="Instagram">
                <svg className="w-4 h-4 text-foreground/40 group-hover:text-terracotta transition-colors" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
              </SocialIcon>
              <SocialIcon href="https://x.com/belosophie" label="X">
                <svg className="w-4 h-4 text-foreground/40 group-hover:text-terracotta transition-colors" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
              </SocialIcon>
            </div>
          </div>

          {/* Navigation */}
          <div>
            <h4 className="font-display text-sm font-semibold text-foreground mb-4">{de ? 'Navigation' : 'Navigation'}</h4>
            <ul className="space-y-2.5">
              {[
                { label: de ? 'Module' : 'Modules', to: '/#features' },
                { label: de ? 'Über Belinda' : 'About Belinda', to: '/about' },
                { label: 'Blog', to: '/blog' },
                { label: de ? 'Preise' : 'Pricing', to: '/pricing' },
              ].map((l, i) => (
                <li key={i}><button onClick={() => nav(l.to)} className="font-body text-sm text-foreground/50 hover:text-terracotta transition-colors">{l.label}</button></li>
              ))}
            </ul>
          </div>

          {/* Module */}
          <div>
            <h4 className="font-display text-sm font-semibold text-foreground mb-4">{de ? 'Module' : 'Modules'}</h4>
            <ul className="space-y-2.5">
              {[
                de ? 'Ernährung' : 'Nutrition',
                'Glow',
              ].map((m, i) => (
                <li key={i} className="font-body text-sm text-foreground/50">{m}</li>
              ))}
              <li><button onClick={() => nav('/modules')} className="font-body text-sm text-foreground/50 hover:text-terracotta transition-colors">{de ? 'Spezialbereiche' : 'Specialist areas'}</button></li>
              <li><button onClick={() => nav('/methode')} className="font-body text-sm text-foreground/50 hover:text-terracotta transition-colors">{de ? 'Methode' : 'Method'}</button></li>
              <li><button onClick={() => nav('/coaching-in-motion')} className="font-body text-sm text-foreground/50 hover:text-terracotta transition-colors">Coaching in Motion</button></li>
            </ul>
          </div>

          {/* Rechtliches */}
          <div>
            <h4 className="font-display text-sm font-semibold text-foreground mb-4">{de ? 'Rechtliches' : 'Legal'}</h4>
            <ul className="space-y-2.5">
              <li><button onClick={() => nav('/privacy')} className="font-body text-sm text-foreground/50 hover:text-terracotta transition-colors" data-testid="footer-privacy">{de ? 'Datenschutz' : 'Privacy Policy'}</button></li>
              <li><button onClick={() => nav('/imprint')} className="font-body text-sm text-foreground/50 hover:text-terracotta transition-colors" data-testid="footer-imprint">{de ? 'Impressum' : 'Imprint'}</button></li>
              <li><button onClick={() => nav('/pricing')} className="font-body text-sm text-foreground/50 hover:text-terracotta transition-colors">{de ? 'AGB' : 'Terms'}</button></li>
              <li><a href="/belosophie-tutorial-user.pdf" target="_blank" rel="noopener noreferrer" className="font-body text-sm text-foreground/50 hover:text-terracotta transition-colors">{de ? 'Tutorial (PDF)' : 'Tutorial (PDF)'}</a></li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-10 pt-6 border-t border-border/30 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="font-body text-xs text-foreground/40">
            2026 belosophie. {de ? 'Alle Rechte vorbehalten.' : 'All rights reserved.'}
          </p>
          <div className="flex items-center gap-1">
            <svg className="w-3 h-3 text-foreground/30" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/></svg>
            <span className="font-body text-xs text-foreground/30">{de ? 'Gemacht in der Schweiz' : 'Made in Switzerland'}</span>
          </div>
          <p className="font-body text-[10px] text-foreground/30 max-w-md text-center md:text-right">
            {de
              ? 'belosophie ist kein Medizinprodukt. Keine Diagnose oder Therapie. Konsultiere immer deine Ärztin.'
              : 'belosophie is not a medical device. No diagnosis or therapy. Always consult your doctor.'}
          </p>
        </div>
      </div>
    </footer>
  );
}
